// GuestOnlyRoute.js
//
// The mirror of ProtectedRoute: keeps a signed-in user off the pages that only
// make sense when signed out.
//
// Without it, an authenticated user could open /loginpage or /registerpage and
// attempt to register again — which now correctly fails as a duplicate, so the
// only possible outcome was an error for something they should never have been
// offered.
//
// Waits for auth hydration before deciding. `user` starts null while the
// session is still being resolved, so redirecting immediately would bounce a
// signed-in user through the login page on every hard refresh.
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const GuestOnlyRoute = ({ children, redirectTo = '/' }) => {
  const { isLoggedIn, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <span
          role="status"
          aria-label="Loading"
          className="h-10 w-10 rounded-full border-[3px] border-gray-100 border-t-brand animate-spin"
        />
      </div>
    );
  }

  if (isLoggedIn) {
    // `replace` so the browser Back button does not drop them straight back
    // onto the page they were just redirected away from.
    return <Navigate to={location.state?.from || redirectTo} replace />;
  }

  return children;
};

export default GuestOnlyRoute;
